// =============================================================================
// features/relatorios/comercial/hooks/useSalesReportsExport.ts — Hook de
// exportação da aba Comercial (F35-S12).
//
// Mesmo padrão de ../../hooks/useExportReport.ts: mutation que baixa o Blob
// e dispara o download no navegador.
//
// Gating (camada UI): só deve ser chamado quando
//   hasPermission('reports:export') && hasPermission('sales_reports:read')
//   && flagEnabled('sales.reports.enabled').
// =============================================================================
import type { SalesReportsExportRequest } from '@elemento/shared-schemas';
import { useMutation } from '@tanstack/react-query';

import { postSalesReportsExport, type SalesReportsExportResult } from '../api';

function triggerDownload(result: SalesReportsExportResult): void {
  const url = URL.createObjectURL(result.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface UseSalesReportsExportResult {
  exportReport: (args: SalesReportsExportRequest) => void;
  isExporting: boolean;
  error: Error | null;
  isSuccess: boolean;
  reset: () => void;
}

export function useSalesReportsExport(): UseSalesReportsExportResult {
  const mutation = useMutation<SalesReportsExportResult, Error, SalesReportsExportRequest>({
    mutationFn: postSalesReportsExport,
    onSuccess: (result) => {
      triggerDownload(result);
    },
  });

  return {
    exportReport: mutation.mutate,
    isExporting: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
}
